import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Crown, Loader2, XCircle } from "lucide-react";
import { API } from "@/lib/api";

const MAX_TRIES = 15;

const statusUrl = (params) => {
  const sub = params.get("subscription_id");
  return sub ? `${API}/payments/paypal/status/${sub}` : `${API}/payments/status/${params.get("session_id")}`;
};

const outcomeOf = (data) => {
  if (data.payment_status === "paid") return "paid";
  if (["expired", "failed"].includes(data.status)) return "failed";
  return null;
};

const usePaymentPoll = (params) => {
  const [state, setState] = useState("checking");
  useEffect(() => {
    const url = statusUrl(params);
    let tries = 0;
    let timer;
    const poll = async () => {
      const outcome = await axios.get(url).then((r) => outcomeOf(r.data)).catch(() => "failed");
      if (outcome) return setState(outcome);
      if (++tries >= MAX_TRIES) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);
  return state;
};

const Shell = ({ children, testId }) => (
  <div className="max-w-lg mx-auto px-4 pt-24 text-center" data-testid={testId}>{children}</div>
);

const Paid = () => (
  <>
    <Crown className="w-10 h-10 text-amber-400 mx-auto" />
    <h1 className="font-display text-3xl font-bold mt-5" data-testid="payment-paid">Benvenuto in Pro!</h1>
    <p className="text-slate-400 mt-3">Ora puoi generare idee senza limiti.</p>
    <Link to="/genera" className="inline-block mt-8 rounded-full bg-amber-500 px-6 py-3 font-semibold text-slate-950 hover:bg-amber-400" data-testid="payment-go-generate">Genera nuove idee</Link>
  </>
);

const Failed = ({ timeout }) => (
  <>
    <XCircle className="w-10 h-10 text-rose-400 mx-auto" />
    <h1 className="font-display text-2xl font-bold mt-5" data-testid="payment-failed">{timeout ? "Conferma in ritardo" : "Pagamento non completato"}</h1>
    <p className="text-slate-400 mt-3">{timeout ? "Se il pagamento è andato a buon fine, recupera il Pro dalla pagina Piani con la tua email tra qualche minuto." : "Nessun addebito effettuato. Puoi riprovare quando vuoi."}</p>
    <Link to="/pro" className="inline-block mt-8 rounded-full border border-white/15 px-6 py-3 font-semibold text-slate-200 hover:text-amber-300">Torna ai piani</Link>
  </>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const state = usePaymentPoll(params);
  return (
    <Shell testId="payment-success-page">
      {state === "checking" && <><Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto" /><p className="text-slate-300 mt-5" data-testid="payment-checking">Stiamo confermando il pagamento…</p></>}
      {state === "paid" && <Paid />}
      {(state === "failed" || state === "timeout") && <Failed timeout={state === "timeout"} />}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel-page">
      <h1 className="font-display text-3xl font-bold">Pagamento annullato</h1>
      <p className="text-slate-400 mt-3">Nessun addebito. Puoi continuare con il piano gratuito o riprovare.</p>
      <Link to="/pro" className="inline-block mt-8 rounded-full bg-amber-500 px-6 py-3 font-semibold text-slate-950 hover:bg-amber-400" data-testid="payment-cancel-back">Torna ai piani</Link>
    </Shell>
  );
}
